import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { frontendserverurl, serverurl } from '../../environments/environment';
import { Company, CompanyActiveCoach, CompanyStructured, GateConfigElement, InviteLink } from '../lib/interfaces/company';
import { CustomLoginFormConfig } from '../lib/interfaces/configdata';
import { DbLicense, License } from '../lib/interfaces/license';
import { PaginatedResult } from '../lib/interfaces/paginated-result.interface';
import { InviteVerification } from '../lib/interfaces/roles';
import { User } from '../lib/interfaces/user';
import { SnackService } from './snack.service';

@Injectable({
    providedIn: 'root'
})
export class CompaniesService {

    // GET Domain/companies/major-only => Only highest Level Companies
    // GET Domain/companies/strutured => Structured Companies
    // POST Domain/companies => Add new Company
    // DELETE Domain/companies/total/:id => Delete a Company with all children

    // Injections
    http = inject(HttpClient);
    snack = inject(SnackService);

    public async deleteInviteLink(id: string): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.delete(serverurl + `/companies/invite/${id}`).subscribe({
                next: () => {
                    this.snack.success({ headline: "Invite Link Deleted", text: "The invite link has been deleted successfully." });
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }
    async getInvitesByCompanyId(companyId: string): Promise<false | InviteLink[]> {
        return await new Promise((resolve) => {
            this.http.get(serverurl + `/companies/invite/${companyId}`).subscribe({
                next: async (data: any) => {
                    resolve(data as InviteLink[]);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }

    async generateInviteLink(
        companyId: string,
        userId: string | null = null,
        roles: string[],
        licenseId: string,
        expire: number | undefined = undefined,
        verificationMethod: InviteVerification,
        usergateConfig: GateConfigElement[] | null,
        loginFormConfig: CustomLoginFormConfig[] | null
    ): Promise<false | string> {
        return await new Promise((resolve) => {
            this.http.post(
                serverurl + '/companies/invite',
                {
                    companyId,
                    userId,
                    roles,
                    expire,
                    licenseId,
                    verificationMethod,
                    usergateConfig,
                    loginFormConfig,
                },
            ).subscribe({
                next: async (data: any) => {
                    resolve(data.id);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }

    async getCoachesByCompanyId(
        companyId: string,
    ): Promise<false | CompanyActiveCoach[]> {
        return await new Promise((resolve) => {
            this.http.get(
                serverurl + '/companies/coaches/' + companyId,
            ).subscribe({
                next: async (data: any) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }

    async addNewCompany(company: Partial<Company>, license: License): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.post(serverurl + '/companies', { company, license }).subscribe({
                next: async (data: any) => {
                    this.snack.success(data.msg);
                    resolve(data.id)
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }

    async deleteCompanyWithChilds(companyId: string) {
        return await new Promise((resolve) => {
            this.http.delete(serverurl + '/companies/total/' + companyId).subscribe({
                next: async (data: any) => {
                    resolve(true)
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }

    async getHighestLevelCompanies(
        offset: number = 0,
        limit: number = 50,
        search?: string,
        filters: string[] = [],
    ): Promise<PaginatedResult<Company> | false> {
        return await new Promise((resolve) => {
            let params = new HttpParams()
                .append('offset', offset)
                .append('limit', limit);

            if (search) {
                params = params.append('search', search);
            }

            if (filters.length) {
                params = params.append('filters', filters.join(','));
            }

            this.http.get<PaginatedResult<Company>>(
                serverurl + `/companies/major-only`,
                { params },
            ).subscribe({
                next: async (data) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }
    async getStrucuredCompanies(): Promise<Company[] | false> {
        return await new Promise((resolve) => {
            this.http.get(serverurl + '/companies/strutured').subscribe({
                next: async (data: any) => {
                    resolve(data as Company[]);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }

    async getCompanyStructureById(companyId: string): Promise<CompanyStructured[]> {
        return new Promise<CompanyStructured[]>((resolve) => {
            this.http.get<CompanyStructured[]>(`${serverurl}/companies/structured/${companyId}`).subscribe({
                next: (data) => resolve(data),
                error: (err) => {
                    this.snack.error(err);
                    resolve([]);
                }
            });
        });
    }
    async getLicenseByCompanyId(companyId: string): Promise<DbLicense | false> {
        return new Promise<DbLicense | false>((resolve) => {
            this.http.get<DbLicense>(`${serverurl}/companies/licensebyid/${companyId}`).subscribe({
                next: (data) => resolve(data as DbLicense),
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async updateCompany(company: Company, license: License): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.http.put<Company[]>(`${serverurl}/companies`, { company, license }).subscribe({
                next: (data: any) => {
                    this.snack.success(data.msg);
                    resolve(true)
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async updateUserForCompany(userData: Partial<User>): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.put(serverurl + '/companies/updateforcompany', { ...userData }).subscribe({
                next: async (data: any) => {
                    this.snack.success(data.msg)
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }
    async addUserForLicense(user: Partial<User>): Promise<User | false> {
        return await new Promise((resolve) => {
            this.http.post(frontendserverurl + '/auth/createforcompany', { ...user }).subscribe({
                next: async (data: any) => {
                    resolve(data.user as User);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }
    async deleteUserFromCompany(userId: string, companyId: string, licenseId: string): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.delete(serverurl + '/companies/user', { body: { userId, companyId, licenseId } }).subscribe({
                next: async (data: any) => {
                    this.snack.success(data.msg);
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            })
        })
    }

    public async uploadLogo(logo: string, companyId: string): Promise<boolean> {
        return await new Promise((resolve) => {
            const form = new FormData();

            form.append('logo', logo);
            form.append('companyId', companyId);

            this.http.post(serverurl + '/companies/logo', form).subscribe({
                next: (data: any) => {
                    this.snack.success(data.msg);
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err.msg);
                    resolve(false);
                }
            });
        });
    }

    public async getLogo(companyId: string): Promise<any> {
        return await new Promise((resolve) => {
            this.http.get(serverurl + `/companies/logo/${companyId}`)
                .subscribe({
                    next: (data: any) => resolve(data),
                    error: (err) => {
                        this.snack.error(err);

                        resolve(false);
                    },
                });
        });
    }
}
